import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { AppIcon, AppIcons, FeaturedIcon } from '@backrs_ui/common-ui';

import { Box, ButtonBase, SxProps, Theme, Typography } from '@mui/material';
import ReactMarkdown, { type Components } from 'react-markdown';

export type GuideStrategyMove = Readonly<{
  title: string;
  body: string;
}>;

export type GuideStrategyPath = Readonly<{
  id: string;
  label: string;
  icon: AppIcons;
  /** Rendered as inline markdown. */
  intro: string;
  moves: readonly GuideStrategyMove[];
}>;

export type GuideStrategySelectorContent = Readonly<{
  heading: string;
  intro: string;
  paths: readonly GuideStrategyPath[];
}>;

type GuideStrategySelectorProps = Readonly<{
  idPrefix: string;
  content: GuideStrategySelectorContent;
}>;

type Connector = Readonly<{ top: number; left: number; width: number }>;

const WIDE_BREAKPOINT = 720;

const INLINE_MARKDOWN: Components = { p: ({ children }) => <>{children}</> };

/** Pill list that opens a detail card to the side, joined by a line to the selected pill. */
export const GuideStrategySelector: React.FC<GuideStrategySelectorProps> = ({
  idPrefix,
  content,
}) => {
  const { heading, intro, paths } = content;

  const [selectedId, setSelectedId] = useState(paths[0]?.id ?? '');
  const [isWide, setIsWide] = useState(false);
  const [connector, setConnector] = useState<Connector | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const recompute = useCallback(() => {
    const root = rootRef.current;

    if (!root) return;

    const wide = root.clientWidth >= WIDE_BREAKPOINT;

    setIsWide(wide);

    const card = cardRef.current;
    const pill = pillRefs.current[selectedId];

    if (!wide || !card || !pill) {
      setConnector(null);

      return;
    }

    const rootRect = root.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();

    setConnector({
      top: pillRect.top + pillRect.height / 2 - rootRect.top,
      left: pillRect.right - rootRect.left,
      width: Math.max(cardRect.left - pillRect.right, 0),
    });
  }, [selectedId]);

  useLayoutEffect(() => {
    recompute();

    const root = rootRef.current;

    if (!root) return undefined;

    const observer = new ResizeObserver(() => recompute());

    observer.observe(root);

    return () => observer.disconnect();
  }, [recompute]);

  useLayoutEffect(() => {
    recompute();
  }, [isWide, recompute]);

  if (paths.length === 0) {
    return null;
  }

  const selected: GuideStrategyPath = paths.find((path) => path.id === selectedId) ?? paths[0];

  return (
    <Box ref={rootRef} sx={rootSx(isWide)}>
      <Box sx={panelSx(isWide)}>
        <Box sx={panelHeaderSx}>
          <Typography variant="text-lg-semibold" sx={headingSx}>
            {heading}
          </Typography>
          <Typography variant="text-sm-regular" sx={panelIntroSx}>
            {intro}
          </Typography>
        </Box>

        <Box sx={dividerSx} />

        <Box sx={pillsSx}>
          {paths.map((path) => {
            const active = path.id === selectedId;

            return (
              <ButtonBase
                key={path.id}
                id={`${idPrefix}-${path.id}-pill`}
                ref={(element: HTMLButtonElement | null) => {
                  pillRefs.current[path.id] = element;
                }}
                sx={pillSx(active)}
                onClick={() => setSelectedId(path.id)}
                aria-pressed={active}
              >
                <AppIcon
                  iconName={path.icon}
                  diameter="16px"
                  color={active ? 'Colors/Text/text-white' : 'Colors/Text/text-secondary'}
                />
                {path.label}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {isWide && connector && (
        <Box
          sx={connectorSx}
          style={{ top: connector.top, left: connector.left, width: connector.width }}
        />
      )}

      <Box ref={cardRef} sx={cardSx}>
        <Box sx={cardHeaderSx}>
          <FeaturedIcon
            iconName={selected.icon}
            size="lg"
            shape="square"
            color="Colors/Text/text-secondary"
          />
          <Box sx={cardHeaderTextSx}>
            <Typography variant="text-lg-semibold" sx={cardTitleSx}>
              {selected.label}
            </Typography>
            <Typography variant="text-sm-regular" sx={cardIntroSx}>
              <ReactMarkdown components={INLINE_MARKDOWN}>{selected.intro}</ReactMarkdown>
            </Typography>
          </Box>
        </Box>

        <Box sx={dividerSx} />

        <Box sx={movesSx}>
          {selected.moves.map((move) => (
            <Box key={move.title} sx={moveSx}>
              <Typography variant="text-md-semibold" sx={moveTitleSx}>
                {move.title}
              </Typography>
              <Typography variant="text-sm-regular" sx={moveBodySx}>
                {move.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const rootSx =
  (isWide: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: isWide ? 'row' : 'column',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-3xl'],
    pb: backrs.spacing['spacing-4xl'],
  });

const panelSx =
  (isWide: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    flexShrink: 0,
    width: isWide ? '300px' : '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-3xl'],
    p: backrs.spacing['spacing-3xl'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    borderRadius: backrs.radius['radius-xl'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  });

const panelHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const panelIntroSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  mx: `calc(-1 * ${backrs.spacing['spacing-3xl']})`,
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
});

const pillsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const pillSx =
  (active: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    ...typography['text-sm-semibold'],
    display: 'inline-flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-sm'],
    py: backrs.spacing['spacing-sm'],
    px: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-full'],
    border: `1px solid ${
      active
        ? backrs.colors['Colors/Background/bg-primary-solid']
        : backrs.colors['Colors/Border/border-primary']
    }`,
    color: active
      ? backrs.colors['Colors/Text/text-white']
      : backrs.colors['Colors/Text/text-primary'],
    backgroundColor: active
      ? backrs.colors['Colors/Background/bg-primary-solid']
      : backrs.colors['Colors/Background/bg-primary'],
    transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
    '&:hover': {
      borderColor: active
        ? backrs.colors['Colors/Background/bg-primary-solid']
        : backrs.colors['Colors/Border/border-brand'],
      backgroundColor: active
        ? backrs.colors['Colors/Background/bg-primary-solid']
        : backrs.colors['Colors/Background/bg-primary-hover'],
    },
  });

const connectorSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  height: '1px',
  backgroundColor: backrs.colors['Colors/Background/bg-primary-solid'],
  pointerEvents: 'none',
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  p: backrs.spacing['spacing-3xl'],
  border: `1px solid ${backrs.colors['Colors/Background/bg-primary-solid']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const cardHeaderTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const cardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const cardIntroSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const movesSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const moveSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const moveTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const moveBodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
